import React from 'react';
import { observer } from 'mobx-react';
import { Formik, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import { merge } from 'lodash';

import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';

import { sortCountries } from 'helpers/helpers';
import FormPhoneField from 'components/FormFields/FormPhoneField';
import FormCheckboxGroup from 'components/FormCheckboxGroup/FormCheckboxGroup';
import FormTextField from 'components/FormFields/FormTextField';
import FormNumberField from 'components/FormFields/FormNumberField';
import FormSelectField from 'components/FormFields/FormSelectField';
import DatePicker from 'components/FormFields/FormDatePicker';
import Switch from 'components/FormFields/Switch';
import Message, { MessageVariant } from 'components/Message/Message';

import { realms } from 'helpers/realms';
import { ToolsStore } from 'stores/admin/toolsStore';
import AccountHistory from './components/AccountHistory';
import { TaxMode, Currency } from 'enums';
import { DateTime } from 'luxon';

export type tabNames =
  | 'errMessage'
  | 'account_history'
  | 'signature_state'
  | 'signature_cancel'
  | 'request_cancel'
  | 'generate_pdf'
  | 'generate_pdf_BoPo'
  | 'phone'
  | 'email'
  | '2fa'
  | 'event'
  | 'claim'
  | 'activate_account'
  | 'delete_account'
  | 'reset_advisor'
  | 'change_advisor_phone'
  | 'change_advisor_email'
  | 'set_partnercode'
  | 'set_constant'
  | 'change_cc'
  | 'transfer_cc'
  | 'change_id'
  | 'generate_csv'
  | 'taxMode'
  | 'generate_amendment'
  | 'reactivate_user'
  | 'delete_statement'
  | 'account_transaction';

interface Props {
  name: tabNames;
  message?: boolean;
  store: ToolsStore;
}
// FIXME: rozdelit na mensi komponenty
const initialValues = {
  errMessage: { occurenceId: '' },
  account_history: { accountId: '' },
  signature_state: { accountId: '' },
  signature_cancel: { accountId: '' },
  request_cancel: { accountId: '' },
  generate_pdf: { accountId: '', type: '', change: false },
  generate_pdf_BoPo: { bopoId: '', type: '', filename: '' },
  phone: { accountId: '', value: '' },
  email: { accountId: '', value: '' },
  '2fa': { email: '', value: '' },
  event: { accountId: '', type: [] },
  claim: { accountId: '', claim: 0 },
  activate_account: { accountId: '' },
  delete_account: { accountId: '' },
  reset_advisor: { realm: '', username: '' },
  change_advisor_phone: { accountId: '', value: '' },
  change_advisor_email: { accountId: '', value: '' },
  set_partnercode: { realm: '', username: '', partnerCode: '' },
  set_constant: { name: '', value: '' },
  change_cc: { accountId: '', value: '' },
  transfer_cc: { pairs: '' },
  change_id: { accountId: '', currentIdentifier: '', targetIdentifier: '', citizenship: '' },
  generate_csv: { accountId: '' },
  taxMode: { identifier: '', taxMode: TaxMode.PPP },
  generate_amendment: { accountId: '' },
  reactivate_user: { email: '' },
  delete_statement: { accountId: '', period: '', type: '' },
  account_transaction: {
    accountId: '',
    type: 'DEPOSIT',
    bucketType: 'CASH',
    amount: { value: '', currency: Currency.CZK },
    tradeDate: DateTime.local(),
    success: true,
  },
};

const ToolsForms: React.FC<Props> = ({ name, message, store }) => {
  const { t } = useTranslation();

  const accountId = (width: 4 | 5) => {
    return (
      <Grid item xs={width}>
        <FormTextField name="accountId" label={t(`general_accountId`)} margin="dense" />
      </Grid>
    );
  };

  const intermediary = (width: 4 | 5) => {
    return (
      <Grid item xs={width}>
        <FormSelectField
          name="realm"
          label={t('backOffice_advisors_intermediaryCode')}
          options={Object.values(realms)
            .filter((r) => !['core', 'endusers'].includes(r))
            .map((r) => {
              return { value: r, label: t(`intermediary_${r.toUpperCase()}_short`) };
            })}
          margin="dense"
        />
      </Grid>
    );
  };

  const renderForm = (formikProps: FormikProps<any>) => {
    const { values } = formikProps;

    switch (name) {
      case 'errMessage':
        return (
          <Grid item xs={12} container alignItems="center">
            <Grid item xs={5}>
              <FormTextField name="occurenceId" label={t(`backOffice_errMessage_occurenceId`)} margin="dense" />
            </Grid>
            <Grid item xs={2}>
              <Button variant="contained" color="primary" onClick={() => store.findErrorMessage(values.occurenceId)}>
                {t('general_confirm')}
              </Button>
            </Grid>
          </Grid>
        );
      case 'account_history':
        return <AccountHistory formikProps={formikProps} />;
      case 'signature_state':
        return (
          <Grid item xs={12} container alignItems="center">
            {accountId(5)}
            <Grid item xs={2}>
              <Button variant="contained" color="primary" onClick={() => store.checkSignature(values.accountId)}>
                {t('general_confirm')}
              </Button>
            </Grid>
          </Grid>
        );
      case 'signature_cancel':
        return (
          <Grid item xs={12} container alignItems="center">
            {accountId(5)}
            <Grid item xs={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  store.showConfirmDialog('backOffice_confirm_cancelSignature', () =>
                    store.cancelSignature(values.accountId),
                  )
                }
              >
                {t('general_confirm')}
              </Button>
            </Grid>
          </Grid>
        );
      case 'request_cancel':
        return (
          <Grid item xs={12} container alignItems="center">
            {accountId(5)}
            <Grid item xs={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  store.showConfirmDialog('backOffice_confirm_cancelRequest', () =>
                    store.cancelRequest(values.accountId),
                  )
                }
              >
                {t('general_confirm')}
              </Button>
            </Grid>
          </Grid>
        );
      case 'generate_pdf':
        return (
          <Grid item xs={12} container alignItems="center">
            {accountId(4)}
            <Grid item xs={4}>
              <FormSelectField
                name="type"
                label={t('general_type')}
                options={[
                  'AML_QUESTIONNAIRE',
                  'MAIN_CONTRACT',
                  'PRICE_LIST',
                  'INVESTMENT_QUESTIONNAIRE',
                  'FINANCIAL_QUESTIONNAIRE',
                  'MEETING_RECORD',
                  'INVESTMENT_PLAN',
                  'INCOME_OWNERSHIP',
                  'PERSONAL_DATA',
                  'LTIP_AGREEMENT',
                  'LTIP_TERMINATION',
                  'CONTRACT_TERMINATION',
                ].map((type) => ({ value: type, label: t(`pdf_document_${type}`) }))}
                margin="dense"
              />
            </Grid>
            <Grid item xs={2}>
              <Switch name={`change`} label={t('backOffice_generate_pdf_change')} />
            </Grid>
            <Grid item xs={2} style={{ textAlign: 'right' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  store.showConfirmDialog('backOffice_confirm_generate_pdf', () =>
                    store.generatePDF(values.accountId, values.type, values.change),
                  )
                }
              >
                {t('general_confirm')}
              </Button>
            </Grid>
          </Grid>
        );
      case 'generate_pdf_BoPo':
        return (
          <Grid item xs={12} container alignItems="center">
            <Grid item xs={4}>
              <FormTextField name="bopoId" label={t(`general_bopoId`)} margin="dense" />
            </Grid>
            <Grid item xs={4}>
              <FormSelectField
                name="type"
                label={t('general_type')}
                options={[
                  'AML_QUESTIONNAIRE',
                  'MAIN_CONTRACT',
                  'PRICE_LIST',
                  'INVESTMENT_QUESTIONNAIRE',
                  'FINANCIAL_QUESTIONNAIRE',
                  'MEETING_RECORD',
                  'INVESTMENT_PLAN',
                  'INCOME_OWNERSHIP',
                  'LTIP_AGREEMENT',
                  'LTIP_TERMINATION',
                  'CONTRACT_TERMINATION',
                ].map((type) => ({ value: type, label: t(`pdf_document_${type}`) }))}
                margin="dense"
              />
            </Grid>
            {/* <Grid item xs={4}>
              <FormTextField
                name="filename"
                label={t(`general_filename`)}
                margin="dense"
                value={`bopo_${formikProps.values.bopoId}_${formikProps.values.type}.pdf`}
              />
            </Grid> */}
            <Grid item xs={2} style={{ textAlign: 'right' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  store.showConfirmDialog('backOffice_confirm_generate_pdf', () =>
                    store.generateBoPoPDF(values.bopoId, values.type, values.filename),
                  )
                }
              >
                {t('general_confirm')}
              </Button>
            </Grid>
          </Grid>
        );
      case 'generate_amendment':
        return (
          <Grid item xs={12} container alignItems="center">
            {accountId(5)}
            <Grid item xs={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  store.showConfirmDialog('backOffice_confirm_generate_amendment', () =>
                    store.generateAmendment(values.accountId),
                  )
                }
              >
                {t('general_confirm')}
              </Button>
            </Grid>
          </Grid>
        );
      case 'delete_statement':
        return (
          <Grid item xs={12} container alignItems="center">
            {accountId(5)}
            <Grid item xs={3}>
              <FormSelectField
                name="type"
                label={t('general_type')}
                options={['MONTH', 'YEAR'].map((type) => ({ value: type, label: t(`pdf_document_${type}`) }))}
                margin="dense"
              />
            </Grid>
            <Grid item xs={4}>
              <FormTextField name="period" label={t(`backOffice_delete_statement_period`)} margin="dense" />
            </Grid>

            <Grid item xs={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => store.deleteStatement(values.accountId, values.period, values.type)}
              >
                {t('general_confirm')}
              </Button>
            </Grid>
          </Grid>
        );
      case 'phone':
        return (
          <Grid item xs={12} container alignItems="center">
            {accountId(5)}
            <Grid item xs={5}>
              <FormPhoneField name="value" label={t('backOffice_phone_value')} margin="dense" />
            </Grid>
            <Grid item xs={2} style={{ textAlign: 'right' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  store.showConfirmDialog('backOffice_confirm_phone', () =>
                    store.changePhoneNumber(values.accountId, values.value),
                  )
                }
              >
                {t('general_confirm')}
              </Button>
            </Grid>
          </Grid>
        );
      case 'email':
        return (
          <Grid item xs={12} container alignItems="center">
            {accountId(5)}
            <Grid item xs={5}>
              <FormTextField name="value" label={t(`backOffice_email_value`)} margin="dense" />
            </Grid>
            <Grid item xs={2} style={{ textAlign: 'right' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  store.showConfirmDialog('backOffice_confirm_email', () =>
                    store.changeEmail(values.accountId, values.value),
                  )
                }
              >
                {t('general_confirm')}
              </Button>
            </Grid>
          </Grid>
        );
      case 'taxMode':
        return (
          <Grid item xs={12} container alignItems="center">
            <Grid item xs={5}>
              <FormTextField name="identifier" label={t(`general_personalId_PERSONAL`)} margin="dense" />
            </Grid>
            <Grid item xs={5}>
              <FormSelectField
                name={`taxMode`}
                label={t('accountDetail_taxMode')}
                options={sortCountries(
                  store.options!.taxMode.map((taxMode) => ({
                    value: taxMode,
                    label: taxMode,
                  })),
                )}
                margin="dense"
                disabled
              />
            </Grid>
            <Grid item xs={2} style={{ textAlign: 'right' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  store.showConfirmDialog('backOffice_confirm_taxMode', () =>
                    store.changeTaxMode(values.identifier, values.taxMode),
                  )
                }
              >
                {t('general_confirm')}
              </Button>
            </Grid>
          </Grid>
        );
      case 'reactivate_user':
        return (
          <Grid item xs={12} container alignItems="center">
            <Grid item xs={5}>
              <FormTextField name="email" label={`${t('general_username')} (${t('general_email')})`} margin="dense" />
            </Grid>
            <Grid item xs={2} style={{ textAlign: 'right' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  store.showConfirmDialog('backOffice_confirm_reactivate_user', () =>
                    store.reactivateUser(values.email),
                  )
                }
              >
                {t('general_confirm')}
              </Button>
            </Grid>
          </Grid>
        );

      case '2fa':
        return (
          <Grid item xs={12} container alignItems="center">
            <Grid item xs={5}>
              <FormTextField name="email" label={t(`general_email`)} margin="dense" />
            </Grid>
            <Grid item xs={5}>
              <FormSelectField
                name="value"
                label={t('backOffice_2fa_type')}
                options={[
                  { value: 'SMS', label: 'SMS' },
                  { value: 'OTP', label: 'OTP' },
                  { value: 'NONE', label: 'bez 2FA' },
                ]}
                margin="dense"
              />
            </Grid>
            <Grid item xs={2} style={{ textAlign: 'right' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  store.showConfirmDialog('backOffice_confirm_2fa', () => store.change2FA(values.email, values.value))
                }
              >
                {t('general_confirm')}
              </Button>
            </Grid>
          </Grid>
        );
      case 'event':
        return (
          <Grid item xs={12} container alignItems="center">
            {accountId(4)}
            <Grid item xs={6}>
              <FormCheckboxGroup
                name={`type`}
                label={t('general_type')}
                values={values['type'] as string[]}
                options={['CONTRACT', 'SIGNATURES', 'PROCESSING']}
                literal
                horizontal
                noMargin
              />
            </Grid>
            <Grid item xs={2} style={{ textAlign: 'right' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  store.showConfirmDialog('backOffice_confirm_event', () =>
                    store.sendEvent(values.accountId, values.type),
                  )
                }
              >
                {t('general_confirm')}
              </Button>
            </Grid>
          </Grid>
        );
      case 'claim':
        return (
          <Grid item xs={12} container alignItems="center">
            {accountId(5)}
            <Grid item xs={3}>
              <FormNumberField
                name="claim"
                label={t(`backOffice_claim_value`)}
                margin="dense"
                endAdornment={t('currency_CZK')}
              />
            </Grid>
            <Grid item xs={2}>
              <FormSelectField
                name="currency"
                label={t('general_currency')}
                options={[
                  { value: 'CZK', label: 'CZK' },
                  { value: 'EUR', label: 'EUR' },
                ]}
                margin="dense"
              />
            </Grid>
            <Grid item xs={2} style={{ textAlign: 'right' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  store.showConfirmDialog('backOffice_confirm_claim', () =>
                    store.changeClaim(values.accountId, values.claim, values.currency),
                  )
                }
              >
                {t('general_confirm')}
              </Button>
            </Grid>
          </Grid>
        );
      case 'account_transaction':
        return (
          <>
            <Grid item xs={12} container alignItems="center">
              {accountId(5)}
              <Grid item xs={3}>
                <FormNumberField name="amount.value" label={t(`general_amount`)} decimalScale={2} margin="dense" />
              </Grid>
              <Grid item xs={2}>
                <FormSelectField
                  name="amount.currency"
                  label={t('general_currency')}
                  options={[
                    { value: 'CZK', label: 'CZK' },
                    { value: 'EUR', label: 'EUR' },
                  ]}
                  margin="dense"
                />
              </Grid>
              <Grid item xs={2} />
              <Grid item xs={2}>
                <FormSelectField
                  name="type"
                  label={t('general_type')}
                  options={[
                    { value: 'DEPOSIT', label: t('backOffice_account_transaction_type_DEPOSIT') },
                    { value: 'WITHDRAWAL', label: t('backOffice_account_transaction_type_WITHDRAWAL') },
                    { value: 'INTEREST', label: t('backOffice_account_transaction_type_INTEREST') },
                    { value: 'EMPLOYER', label: t('backOffice_account_transaction_type_EMPLOYER') },
                  ]}
                  margin="dense"
                />
              </Grid>
              <Grid item xs={2}>
                <FormSelectField
                  name="bucketType"
                  label={t('general_to')}
                  options={[
                    { value: 'CASH', label: t('accountDetail_overview_movement_CASH') },
                    { value: 'K1', label: t('accountDetail_overview_movement_K1') },
                    { value: 'K2', label: t('accountDetail_overview_movement_K2') },
                    { value: 'K3', label: t('accountDetail_overview_movement_K3') },
                  ]}
                  margin="dense"
                />
              </Grid>

              <Grid item xs={3}>
                <DatePicker
                  maxDate={DateTime.local()}
                  minDate={DateTime.local().minus({ years: 1 })}
                  name={`tradeDate`}
                  label={t('general_date')}
                />
              </Grid>
              <Grid item xs={3}>
                <Switch name={`success`} label={t('backOffice_account_transaction_success')} />
              </Grid>
              <Grid item xs={2} style={{ textAlign: 'right' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() =>
                    store.showConfirmDialog('backOffice_confirm_account_transaction', () =>
                      store.accountTransaction(
                        values.accountId,
                        values.type,
                        values.bucketType,
                        values.amount,
                        DateTime.fromISO(values.tradeDate).toFormat('yyyy-MM-dd').toString(),
                        values.success,
                      ),
                    )
                  }
                >
                  {t('general_confirm')}
                </Button>
              </Grid>
            </Grid>
          </>
        );
      case 'activate_account':
        return (
          <Grid item xs={12} container alignItems="center">
            {accountId(5)}
            <Grid item xs={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  store.showConfirmDialog('backOffice_confirm_activate_account', () =>
                    store.activateAccount(values.accountId),
                  )
                }
              >
                {t('general_confirm')}
              </Button>
            </Grid>
          </Grid>
        );
      case 'delete_account':
        return (
          <Grid item xs={12} container alignItems="center">
            {accountId(5)}
            <Grid item xs={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  store.showConfirmDialog('backOffice_confirm_delete_account', () =>
                    store.deleteAccount(values.accountId),
                  )
                }
              >
                {t('general_confirm')}
              </Button>
            </Grid>
          </Grid>
        );
      case 'reset_advisor':
        return (
          <Grid item xs={12} container alignItems="center">
            {intermediary(5)}
            <Grid item xs={5}>
              <FormTextField name="username" label={t(`general_username`)} margin="dense" />
            </Grid>
            <Grid item xs={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  store.showConfirmDialog('backOffice_reset_advisor_confirm', () =>
                    store.resetAdvisor(values.realm, values.username),
                  )
                }
              >
                {t('general_confirm')}
              </Button>
            </Grid>
          </Grid>
        );
      case 'change_advisor_phone':
        return (
          <Grid item xs={12} container alignItems="center">
            {accountId(5)}
            <Grid item xs={5}>
              <FormPhoneField name="value" label={t('backOffice_change_advisor_phone_value')} margin="dense" />
            </Grid>
            <Grid item xs={2} style={{ textAlign: 'right' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  store.showConfirmDialog('backOffice_confirm_change_advisor_phone', () =>
                    store.changeAdvisorPhone(values.accountId, values.value),
                  )
                }
              >
                {t('general_confirm')}
              </Button>
            </Grid>
          </Grid>
        );
      case 'change_advisor_email':
        return (
          <Grid item xs={12} container alignItems="center">
            {accountId(5)}
            <Grid item xs={5}>
              <FormTextField name="value" label={t(`backOffice_change_advisor_email_value`)} margin="dense" />
            </Grid>
            <Grid item xs={2} style={{ textAlign: 'right' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  store.showConfirmDialog('backOffice_confirm_change_advisor_email', () =>
                    store.changeAdvisorEmail(values.accountId, values.value),
                  )
                }
              >
                {t('general_confirm')}
              </Button>
            </Grid>
          </Grid>
        );
      case 'set_partnercode':
        return (
          <Grid item xs={12} container alignItems="center">
            {intermediary(4)}
            <Grid item xs={4}>
              <FormTextField name="username" label={t(`general_username`)} margin="dense" />
            </Grid>
            <Grid item xs={2}>
              <FormTextField name="partnerCode" label={t(`backOffice_set_partnercode_partnercode`)} margin="dense" />
            </Grid>
            <Grid item xs={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  store.showConfirmDialog('backOffice_set_partnercode_confirm', () =>
                    store.setPartnerCode(values.realm, values.username, values.partnerCode),
                  )
                }
              >
                {t('general_confirm')}
              </Button>
            </Grid>
          </Grid>
        );
      case 'set_constant':
        return (
          <Grid item xs={12} container alignItems="center">
            <Grid item xs={5}>
              <FormTextField name="name" label={t(`general_title`)} margin="dense" />
            </Grid>
            <Grid item xs={5}>
              <FormTextField name="value" label={t(`general_value`)} margin="dense" />
            </Grid>
            <Grid item xs={2} style={{ textAlign: 'right' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  store.showConfirmDialog('backOffice_confirm_set_constant', () =>
                    store.setConstant(values.name, values.value),
                  )
                }
              >
                {t('general_confirm')}
              </Button>
            </Grid>
          </Grid>
        );
      case 'generate_csv':
        return (
          <Grid item xs={12} container alignItems="center">
            {accountId(5)}
            <Grid item xs={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  store.showConfirmDialog('backOffice_confirm_generate_csv', () => store.generateCsv(values.accountId))
                }
              >
                {t('general_confirm')}
              </Button>
            </Grid>
          </Grid>
        );
      case 'change_cc':
        return (
          <Grid item xs={12} container alignItems="center">
            {accountId(5)}
            <Grid item xs={5}>
              <FormTextField name="value" label={t('backOffice_change_cc_value')} margin="dense" />
            </Grid>
            <Grid item xs={2} style={{ textAlign: 'right' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  store.showConfirmDialog('backOffice_confirm_change_cc', () =>
                    store.changeCC(values.accountId, values.value),
                  )
                }
              >
                {t('general_confirm')}
              </Button>
            </Grid>
          </Grid>
        );
      case 'transfer_cc':
        return (
          <Grid item xs={12} container alignItems="center">
            <Grid item xs={10}>
              <FormTextField
                name="pairs"
                label={t(`backOffice_transfer_cc_value`)}
                margin="dense"
                multiline={true}
                minRows={10}
                maxLength={10000}
              />
            </Grid>
            <Grid item xs={2} style={{ textAlign: 'right' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  store.showConfirmDialog('backOffice_confirm_transfer_cc', () => store.transferCC(values.pairs))
                }
              >
                {t('general_confirm')}
              </Button>
            </Grid>
          </Grid>
        );
      case 'change_id':
        return (
          <Grid item xs={12} container alignItems="center">
            {accountId(5)}
            <Grid item xs={5}>
              <FormSelectField
                name={`citizenship`}
                label={t('general_citizenship')}
                options={sortCountries(
                  store.options!.countryCode.map((country) => ({
                    value: country,
                    label: t(`country_${country}`),
                  })),
                )}
                margin="dense"
              />
            </Grid>
            <Grid item xs={2} />
            <Grid item xs={5}>
              <FormTextField
                name="currentIdentifier"
                label={t(`backOffice_change_id_currentIdentifier`)}
                margin="dense"
              />
            </Grid>
            <Grid item xs={5}>
              <FormTextField
                name="targetIdentifier"
                label={t(`backOffice_change_id_targetIdentifier`)}
                margin="dense"
              />
            </Grid>
            <Grid item xs={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  store.showConfirmDialog('backOffice_confirm_change_id', () =>
                    store.changeID(
                      values.accountId,
                      values.currentIdentifier,
                      values.targetIdentifier,
                      values.citizenship,
                    ),
                  )
                }
              >
                {t('general_confirm')}
              </Button>
            </Grid>
          </Grid>
        );
    }
  };

  return (
    <>
      {message && (
        <Message
          id={`backOffice_${name}_info`}
          message={t(`backOffice_${name}_info`)}
          variant={MessageVariant.LIGHT}
          permanent
        />
      )}
      <Formik initialValues={merge({}, initialValues[name], {})} onSubmit={() => {}} enableReinitialize>
        {(formikProps) => <Grid container>{renderForm(formikProps)}</Grid>}
      </Formik>
    </>
  );
};

export default observer(ToolsForms);
